/**
 * useCrew Hook - THE ONLY WAY TO ACCESS CREW DATA
 *
 * FT-05: Now delegates to useFleetCrew (org-scoped) for fleet-wide crew management.
 * Returns vesselCrew (pre-filtered for current vessel) to maintain existing behavior.
 *
 * Usage:
 *   const { crew, addCrew, updateCrew, deleteCrew } = useCrew();
 */

import { useCallback, useMemo } from 'react';
import { useFleetCrew } from './domain/fleet/useFleetCrew';
import { useVessel } from '../contexts/VesselContext';

// FT-05 Commit 8: Per-vessel duty status check
// This is a wrapper that gets passed to components - they call isOnDuty(member)
// and the currentVesselId is captured from context
const createIsOnDutyHelper = (currentVesselId) => (member) => {
  if (!member || !currentVesselId) return false;
  const dutyStatus = member.dutyStatus || {};
  return dutyStatus[currentVesselId] === 'on_duty';
};

export const useCrew = (options = {}) => {
  const { currentVesselId } = useVessel();
  const { includeInactive = false } = options;

  // FT-05: Use fleet crew hook for org-scoped operations
  const {
    list,           // All org crew (for org-level operations)
    vesselCrew,     // Pre-filtered for current vessel (stable reference)
    add,
    update,
    archive,
    restore,
    remove,
    getById,
    getActive,
    getArchived,
    getOnboard,
    toggleOnboard,
    getOnDuty,
    toggleDutyStatus,
    totalCrew,
    activeCrew,
    archivedCrew,
    onboardCrew,
    onDutyCrew,
    offDutyCrew,
    // FT-05: Fleet-specific operations
    assignToVessel,
    removeFromVessel,
    getByVessel
  } = useFleetCrew();

  // FT-05: Use vesselCrew (pre-filtered for current vessel)
  // This maintains existing behavior - components only see crew assigned to their vessel
  const crew = useMemo(() => {
    if (includeInactive) return vesselCrew;
    // Show all vessel crew except archived
    return vesselCrew.filter(m => m.status !== 'archived');
  }, [includeInactive, vesselCrew]);

  // Crew operations with backward compatibility
  const addCrew = useCallback((crewData) => {
    // FT-05: When adding crew, auto-assign to current vessel
    const dataWithVessel = {
      ...crewData,
      homeVessel: currentVesselId,
      assignedVessels: currentVesselId ? [currentVesselId] : []
    };
    const result = add(dataWithVessel);
    if (result) {
      window.dispatchEvent(new CustomEvent('crewUpdated', {
        detail: { action: 'add', crewId: result.id }
      }));
    }
    return result?.id || null;
  }, [add, currentVesselId]);

  const updateCrew = useCallback((id, updates) => {
    const success = update(id, updates);
    if (success) {
      window.dispatchEvent(new CustomEvent('crewUpdated', {
        detail: { action: 'update', crewId: id }
      }));
    }
    return success;
  }, [update]);

  // FT-175 FIX: deleteCrew now uses archive() to set status='archived'
  // This matches RA pattern - vesselCrew filters out status='archived'
  // The tombstone (deletedAt) is for sync propagation, but UI filters on status
  const deleteCrew = useCallback((id) => {
    const success = archive(id);
    if (success) {
      window.dispatchEvent(new CustomEvent('crewUpdated', {
        detail: { action: 'delete', crewId: id }
      }));
    }
    return success;
  }, [archive]);

  const getCrewById = useCallback((id) => {
    return getById(id);
  }, [getById]);

  // FT-05: Eligibility operations - now implemented via useFleetCrew
  const assignCrewToVessel = useCallback((crewId, vesselId, role = 'authorized', position = null) => {
    const success = assignToVessel(crewId, vesselId);
    if (success) {
      window.dispatchEvent(new CustomEvent('crewUpdated', {
        detail: { action: 'assignToVessel', crewId, vesselId }
      }));
    }
    return success;
  }, [assignToVessel]);

  const removeCrewFromVesselFn = useCallback((crewId, vesselId) => {
    const success = removeFromVessel(crewId, vesselId);
    if (success) {
      window.dispatchEvent(new CustomEvent('crewUpdated', {
        detail: { action: 'removeFromVessel', crewId, vesselId }
      }));
    }
    return success;
  }, [removeFromVessel]);

  const getCrewAssignments = useCallback((crewId) => {
    const member = getById(crewId);
    if (!member) return [];
    return member.assignedVessels || [];
  }, [getById]);

  const getVesselCrew = useCallback((vesselId) => {
    return getByVessel(vesselId);
  }, [getByVessel]);

  // Pre-Departure Wizard: Toggle crew onboard/ashore status (DEPRECATED - use toggleCrewDutyStatus)
  const toggleCrewOnboard = useCallback((crewId) => {
    const success = toggleOnboard(crewId);
    if (success) {
      window.dispatchEvent(new CustomEvent('crewUpdated', {
        detail: { action: 'toggleOnboard', crewId }
      }));
    }
    return success;
  }, [toggleOnboard]);

  // Pre-Departure Wizard: Get onboard crew (DEPRECATED - use getOnDutyCrew)
  const getOnboardCrew = useCallback(() => {
    return getOnboard();
  }, [getOnboard]);

  // FT-05 Commit 8: Toggle crew duty status for current vessel
  const toggleCrewDutyStatus = useCallback((crewId) => {
    const success = toggleDutyStatus(crewId, currentVesselId);
    if (success) {
      window.dispatchEvent(new CustomEvent('crewUpdated', {
        detail: { action: 'toggleDutyStatus', crewId, vesselId: currentVesselId }
      }));
    }
    return success;
  }, [toggleDutyStatus, currentVesselId]);

  // FT-05 Commit 8: Get crew who are On Duty for current vessel
  const getOnDutyCrew = useCallback(() => {
    return getOnDuty(currentVesselId);
  }, [getOnDuty, currentVesselId]);

  // FT-05 Commit 8: Memoized isOnDuty helper bound to current vessel
  const isOnDutyHelper = useMemo(() => {
    return createIsOnDutyHelper(currentVesselId);
  }, [currentVesselId]);

  // For backward compatibility with old code that used setValue
  const setValue = useCallback((newCrew) => {
    // This is a dangerous operation - should be avoided
    // But keeping for backward compatibility
    return false;
  }, []);

  return {
    // Data
    crew,
    loading: false, // Domain hook is synchronous

    // Operations
    addCrew,
    updateCrew,
    deleteCrew,
    getCrewById,
    archiveCrew: archive,
    restoreCrew: restore,

    // FT-05: Eligibility operations (now functional)
    assignCrewToVessel,
    removeCrewFromVessel: removeCrewFromVesselFn,
    getCrewAssignments,
    getVesselCrew,

    // Backward compatibility
    setValue,
    reloadCrew: () => {}, // No-op, data is reactive

    // Additional info
    currentVesselId,
    totalCrew,
    activeCrew,

    // Pre-Departure Wizard (DEPRECATED - use duty status functions below)
    toggleCrewOnboard,
    getOnboardCrew,
    onboardCount: onboardCrew,

    // FT-05 Commit 8: Per-vessel duty status functions
    toggleCrewDutyStatus,  // Toggle duty for current vessel
    getOnDutyCrew,         // Get crew who are On Duty for current vessel
    onDutyCount: onDutyCrew,
    offDutyCount: offDutyCrew,
    isOnDuty: isOnDutyHelper,  // Helper bound to current vessel: isOnDuty(member) => boolean
  };
};
